import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { byId } from "@/lib/format";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { KeySquare, CheckCircle2, Eye, EyeOff } from "lucide-react";

function ServicePlans({ plans }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  if (!Array.isArray(plans) || plans.length === 0) return null;
  const shown = open ? plans : plans.slice(0, 6);
  return (
    <div className="mt-3" data-testid="license-service-plans">
      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
        {t("lic.apps")} ({plans.length})
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {shown.map((sp) => {
          const ok = (sp.provisioningStatus || "Success") === "Success";
          return (
            <span
              key={sp.servicePlanId || sp.rawName || sp.servicePlanName}
              title={sp.rawName || ""}
              className={`flex items-center gap-1 border px-1.5 py-0.5 text-[10px] ${ok ? "border-zinc-200 bg-zinc-50 text-zinc-600" : "border-amber-200 bg-amber-50 text-amber-700"}`}
            >
              {ok && <CheckCircle2 size={9} className="text-emerald-500" />}
              {sp.servicePlanName || sp.rawName || sp}
            </span>
          );
        })}
        {plans.length > 6 && (
          <button
            onClick={() => setOpen(!open)}
            data-testid="license-plans-toggle"
            className="border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
          >
            {open ? t("lic.showLess") : `+${plans.length - 6} ${t("lic.showAll")}`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Licenses() {
  const { t } = useLang();
  const { can } = useAuth();
  const canManage = can("settings.manage");
  const [lic, setLic] = useState([]);
  const [groups, setGroups] = useState([]);
  const [licSort, setLicSort] = useState(() => localStorage.getItem("sort:licenses") || "name");
  const [visFilter, setVisFilter] = useState(() => localStorage.getItem("filter:licenses") || "all");
  useEffect(() => { localStorage.setItem("sort:licenses", licSort); }, [licSort]);
  useEffect(() => { localStorage.setItem("filter:licenses", visFilter); }, [visFilter]);

  const loadLicenses = () => api.get("/licenses").then((r) => setLic(r.data));
  useEffect(() => {
    loadLicenses();
    api.get("/groups").then((r) => setGroups(r.data));
  }, []);

  const toggleHide = async (l) => {
    const next = !l.hidden;
    setLic((prev) => prev.map((x) => (x.skuId === l.skuId ? { ...x, hidden: next } : x)));
    try {
      await api.put(`/licenses/${l.skuId}/visibility`, { hidden: next, note: l.hiddenNote || "" });
      toast.success(next ? t("lic.hidden") : t("lic.shown"));
    } catch (e) {
      setLic((prev) => prev.map((x) => (x.skuId === l.skuId ? { ...x, hidden: !next } : x)));
      toast.error(apiErr(e));
    }
  };

  const saveNote = async (l, note) => {
    if ((l.hiddenNote || "") === (note || "")) return;
    setLic((prev) => prev.map((x) => (x.skuId === l.skuId ? { ...x, hiddenNote: note } : x)));
    try {
      await api.put(`/licenses/${l.skuId}/visibility`, { hidden: true, note });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const skuMap = byId(lic, "skuId");
  const licGroups = groups.filter((g) => g.classification === "licensing");
  const usage = (l) => (l.totalUnits ? l.consumedUnits / l.totalUnits : 0);
  const hiddenCount = useMemo(() => lic.filter((l) => l.hidden).length, [lic]);

  const displayLic = useMemo(() => {
    let arr = [...lic];
    if (visFilter === "visible") arr = arr.filter((l) => !l.hidden);
    else if (visFilter === "hidden") arr = arr.filter((l) => l.hidden);
    arr.sort((a, b) => {
      if (licSort === "usage") return usage(b) - usage(a);
      if (licSort === "available") return (b.totalUnits - b.consumedUnits) - (a.totalUnits - a.consumedUnits);
      return (a.displayName || "").localeCompare(b.displayName || "");
    });
    return arr;
  }, [lic, licSort, visFilter]);

  // 2 columns for up to 6 licenses, 3 columns when more than 6.
  const gridCols = displayLic.length > 6 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2";

  return (
    <>
      <PageHeader title={t("lic.title")} subtitle={t("lic.subtitle")} />
      <div className="space-y-10 p-6 md:p-10">
        <div className="flex flex-wrap items-center justify-end gap-3">
          {hiddenCount > 0 && (
            <span className="mr-auto text-xs text-zinc-400" data-testid="licenses-hidden-count">
              {t("lic.hiddenCount").replace("{n}", hiddenCount)}
            </span>
          )}
          <Select value={visFilter} onValueChange={setVisFilter}>
            <SelectTrigger className="w-44 rounded-sm border-zinc-300 text-sm" data-testid="licenses-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("lic.filterAll")}</SelectItem>
              <SelectItem value="visible">{t("lic.filterVisible")}</SelectItem>
              <SelectItem value="hidden">{t("lic.filterHidden")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={licSort} onValueChange={setLicSort}>
            <SelectTrigger className="w-52 rounded-sm border-zinc-300 text-sm" data-testid="licenses-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">{t("common.sort")}: {t("lic.sortName")}</SelectItem>
              <SelectItem value="usage">{t("common.sort")}: {t("lic.sortUsage")}</SelectItem>
              <SelectItem value="available">{t("common.sort")}: {t("lic.sortAvailable")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {displayLic.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-6 text-sm text-zinc-400" data-testid="licenses-empty">{t("lic.none")}</div>
        ) : (
          <div className={`grid gap-px bg-zinc-200 ${gridCols}`}>
            {displayLic.map((l) => {
              const pct = l.totalUnits ? Math.round((l.consumedUnits / l.totalUnits) * 100) : 0;
              const avail = l.totalUnits - l.consumedUnits;
              const tight = pct >= 90;
              return (
                <div key={l.skuId} className={`bg-white p-5 ${l.hidden ? "opacity-60" : ""}`} data-testid={`license-row-${l.skuId}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-bold text-zinc-900">
                        {l.displayName}
                        {l.hidden && <span title={l.hiddenNote || ""} className="border border-zinc-200 bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{t("lic.hiddenBadge")}</span>}
                      </div>
                      <div className="font-mono text-xs text-zinc-400">{l.skuPartNumber}</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="text-right">
                        <span className="font-display text-2xl font-black tracking-tighter text-zinc-950">{l.consumedUnits}</span>
                        <span className="text-sm text-zinc-400"> / {l.totalUnits}</span>
                        <div className={`text-xs font-semibold ${tight ? "text-red-600" : "text-emerald-600"}`}>{avail} {t("lic.seatsAvailable")}</div>
                      </div>
                      {canManage && (
                        <button
                          onClick={() => toggleHide(l)}
                          data-testid={`license-toggle-hide-${l.skuId}`}
                          title={l.hidden ? t("lic.show") : t("lic.hide")}
                          className="mt-0.5 p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-primary"
                        >
                          {l.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 h-2 w-full bg-zinc-100"><div className={`h-2 ${tight ? "bg-red-500" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>
                  {l.hidden && canManage && (
                    <input
                      defaultValue={l.hiddenNote || ""}
                      placeholder={t("lic.notePlaceholder")}
                      data-testid={`license-note-${l.skuId}`}
                      onBlur={(e) => saveNote(l, e.target.value.trim())}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="mt-3 w-full border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600 placeholder:text-zinc-400 focus:border-primary focus:outline-none"
                    />
                  )}
                  {l.hidden && !canManage && l.hiddenNote && <p className="mt-2 text-xs italic text-zinc-400">{l.hiddenNote}</p>}
                  <ServicePlans plans={l.servicePlans} />
                </div>
              );
            })}
          </div>
        )}

        <div>
          <h2 className="mb-3 font-display text-lg font-black uppercase tracking-tight text-zinc-900">{t("lic.groups")}</h2>
          {licGroups.length === 0 ? (
            <div className="border border-zinc-200 bg-white p-6 text-sm text-zinc-400">No licensing groups identified.</div>
          ) : (
            <div className="grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-2 lg:grid-cols-3">
              {licGroups.map((g) => (
                <div key={g.id} className="bg-white p-5" data-testid={`lic-group-${g.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex h-9 w-9 items-center justify-center bg-amber-100 text-amber-700"><KeySquare size={18} /></div>
                    <span className="font-display text-xl font-black tracking-tighter text-zinc-950">{g.memberCount ?? 0}<span className="ml-1 text-xs font-normal text-zinc-400">{t("lic.members")}</span></span>
                  </div>
                  <div className="mt-3 text-sm font-bold text-zinc-900">{g.displayName}</div>
                  <div className="mt-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("lic.assigns")}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(g.assignedLicenses || []).length === 0 && <span className="text-sm text-zinc-300">—</span>}
                      {(g.assignedLicenses || []).map((id) => {
                        const sku = skuMap[typeof id === "string" ? id : id.skuId];
                        return <span key={typeof id === "string" ? id : id.skuId} className="border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">{sku?.displayName || sku?.skuPartNumber || (typeof id === "string" ? id : id.skuId)}</span>;
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
